import { z } from 'zod';
import { PROJECT_STATUSES, ACTIVITY_STATUSES, EFFORT_LEVEL_CODES, ACTIVITY_WORK_MODES, RANK_TYPES, AGENDA_STATUSES, ISSUE_REPORT_STATUSES, ISSUE_REPORT_TYPES, WORK_LOCATIONS, DAY_TYPES, SUMMARY_MAX, CONTENT_MAX, STORYBOARD_FAVORITE_MAX, WORKLOG_BULK_MAX, IMPORTANCE_LEVELS } from './constants.js';

// === Auth ===

export const loginSchema = z.object({
  username: z.string().min(1, '아이디를 입력해주세요'),
  password: z.string().min(1, '비밀번호를 입력해주세요'),
});

export const registerSchema = z.object({
  username: z.string().min(3, '아이디는 3자 이상이어야 합니다').max(50),
  password: z.string().min(8, '비밀번호는 8자 이상이어야 합니다').max(100),
  name: z.string().min(1, '이름을 입력해주세요').max(100),
  email: z.string().email('올바른 이메일 형식이 아닙니다').nullish(),
  department_id: z.number().int().positive().nullish(),
  position: z.string().max(50).nullish(),
  rank: z.enum(RANK_TYPES).nullish(),
});

export const changePasswordSchema = z.object({
  old_password: z.string().min(1, '현재 비밀번호를 입력해주세요'),
  new_password: z.string().min(8, '새 비밀번호는 8자 이상이어야 합니다').max(100),
});

// === Project ===

export const projectCreateSchema = z.object({
  project_number: z.string().max(20).nullish(), // 수동 입력 시
  classification_id: z.number().int().positive({ message: '분류를 선택해주세요' }),
  year: z.number().int().min(2020).max(2099),
  client_id: z.number().int().positive({ message: '고객사를 선택해주세요' }),
  department_id: z.number().int().positive({ message: '부서를 선택해주세요' }),
  client_contact_id: z.number().int().positive().nullish(),
  name: z.string().min(1, '프로젝트명을 입력해주세요').max(200),
  description: z.string().nullish(),
  content: z.string().nullish(),
  estimated_amount: z.number().int().min(0).nullish(),
  nas_path: z.string().max(500).nullish(),
  start_date: z.string().nullish(), // ISO date string
  end_date: z.string().nullish(),
  pm_id: z.number().int().positive().nullish(),
  assignee_ids: z.array(z.number().int().positive()).optional(),
  status: z.enum(PROJECT_STATUSES).default('draft'),
  importance: z.enum(IMPORTANCE_LEVELS).default('D'),
  notes: z.string().nullish(),
  activities: z.array(z.lazy(() => activityInlineCreateSchema)).optional(),
});

export const projectUpdateSchema = z.object({
  classification_id: z.number().int().positive().optional(),
  year: z.number().int().min(2020).max(2099).optional(),
  client_id: z.number().int().positive().optional(),
  department_id: z.number().int().positive().optional(),
  client_contact_id: z.number().int().positive().nullish(),
  name: z.string().min(1).max(200).optional(),
  description: z.string().nullish(),
  content: z.string().nullish(),
  estimated_amount: z.number().int().min(0).nullish(),
  nas_path: z.string().max(500).nullish(),
  start_date: z.string().nullish(),
  end_date: z.string().nullish(),
  status: z.enum(PROJECT_STATUSES).optional(),
  importance: z.enum(IMPORTANCE_LEVELS).optional(),
  pm_id: z.number().int().positive().nullish(),
  assignee_ids: z.array(z.number().int().positive()).optional(),
  notes: z.string().nullish(),
  version: z.number().int().positive(),
});

// 담당 마스터 지정/해제 — 상세 인원패널 인라인 편집. null = 미지정.
export const projectResponsibleMasterSchema = z.object({
  responsible_master_id: z.number().int().positive().nullable(),
  version: z.number().int().positive(),
});

export const projectStatusSchema = z.object({
  status: z.enum(PROJECT_STATUSES),
  version: z.number().int().positive(),
});

// 프로젝트 담당자 일괄 교체(reconcile). PUT /projects/:id/assignees
export const projectAssigneesUpdateSchema = z.object({
  user_ids: z.array(z.number().int().positive()),
});

// === Activity ===

export const activityInlineCreateSchema = z.object({
  category_id: z.number().int().positive({ message: '중분류를 선택해주세요' }),
  subcategory_id: z.number().int().positive().nullish(),
  title: z.string().max(200).nullish(),
  assignee_user_ids: z.array(z.number().int().positive()).nullish(),
  assignee_team_ids: z.array(z.number().int().positive()).nullish(),
  primary_assignee_id: z.number().int().positive().nullish(),
  start_date: z.string().nullish(),
  target_date: z.string().nullish(),
  // 사후등록: 완료일을 지정하면 가등록/대기를 건너뛰고 곧바로 완료 상태로 생성된다.
  // 미래 날짜 금지·시작일 역전 금지는 서버(KST 기준)에서 최종 검증한다.
  end_date: z.string().nullish(),
  effort_level: z.enum(EFFORT_LEVEL_CODES).default('normal'),
  // 근무 형태 — 미지정(null)이 기본. 분류와 직교한 축이라 분류 선택을 대체하지 않는다.
  work_mode: z.enum(ACTIVITY_WORK_MODES).nullish(),
  progress: z.number().int().min(0).max(100).default(0),
  plan_description: z.string().nullish(),
  result_description: z.string().nullish(),
  notes: z.string().nullish(),
  status: z.enum(ACTIVITY_STATUSES).default('pending'),
  sort_order: z.number().int().min(0).nullish(),
});

export const activityCreateSchema = activityInlineCreateSchema;

export const activityUpdateSchema = z.object({
  category_id: z.number().int().positive().optional(),
  subcategory_id: z.number().int().positive().nullish(),
  title: z.string().max(200).nullish(),
  start_date: z.string().nullish(),
  target_date: z.string().nullish(),
  end_date: z.string().nullish(),
  effort_level: z.enum(EFFORT_LEVEL_CODES).optional(),
  work_mode: z.enum(ACTIVITY_WORK_MODES).nullish(),
  progress: z.number().int().min(0).max(100).optional(),
  status: z.enum(ACTIVITY_STATUSES).optional(),
  plan_description: z.string().nullish(),
  result_description: z.string().nullish(),
  notes: z.string().nullish(),
  sort_order: z.number().int().min(0).optional(),
  cancel_reason: z.string().max(500).nullish(), // PUT 경로로 status='cancelled' 전환 시 사유(방어용)
  version: z.number().int().positive(),
});

export const activityStatusSchema = z
  .object({
    status: z.enum(ACTIVITY_STATUSES),
    version: z.number().int().positive(),
    reason: z.string().max(500).nullish(), // '변경/중단'(cancelled) 전환 시 필수
  })
  .refine(
    (d) => d.status !== 'cancelled' || (typeof d.reason === 'string' && d.reason.trim().length > 0),
    { message: '변경/중단 상태로 변경하려면 사유를 입력해야 합니다', path: ['reason'] },
  );

export const activityEffortSchema = z.object({
  effort_level: z.enum(EFFORT_LEVEL_CODES),
  version: z.number().int().positive(),
});

export const activityProgressSchema = z.object({
  progress: z.number().int().min(0).max(100),
  version: z.number().int().positive(),
});

export const activityUrgentSchema = z.object({
  is_urgent: z.boolean(),
  version: z.number().int().positive(),
});

// === Activity Approval ===

export const activityApproveSchema = z.object({
  version: z.number().int().positive(),
});

export const activityRejectSchema = z.object({
  version: z.number().int().positive(),
  reason: z.string().max(500).nullish(),
});

export const bulkApproveSchema = z.object({
  activity_ids: z.array(z.number().int().positive()).min(1).max(50),
});
export type BulkApproveInput = z.infer<typeof bulkApproveSchema>;

// === Deliverable ===

export const deliverableCreateSchema = z.object({
  label: z.string().min(1, '라벨을 입력해주세요').max(255),
  nas_path: z.string().min(1, 'NAS 경로를 입력해주세요').max(500),
});

export const deliverableUpdateSchema = z.object({
  label: z.string().min(1, '라벨을 입력해주세요').max(255).optional(),
  nas_path: z.string().min(1, 'NAS 경로를 입력해주세요').max(500).optional(),
});

// === Admin ===

export const userApproveSchema = z.object({
  role: z.enum(['user', 'admin', 'master']),
  department_id: z.number().int().positive().nullish(),
  position: z.string().max(50).nullish(),
  position_order: z.number().int().min(0).nullish(),
  rank: z.enum(RANK_TYPES).nullish(),
  as_team_id: z.number().int().positive().nullish(),
  is_as_eligible: z.boolean().nullish(),
});

// === Settings ===

export const settingsUpdateSchema = z.object({
  storyboard_after_hours_tab: z.enum(['new_row', 'content']).optional(),
  favorite_clients: z.array(z.number().int().positive()).optional(),
  favorite_projects: z.array(z.number().int().positive()).optional(),
  hidden_subcategories: z.array(z.number().int().positive()).optional(),
  hidden_categories: z.array(z.number().int().positive()).optional(),
  default_category_id: z.number().int().positive().nullable().optional(),
  meal_alert_enabled: z.boolean().optional(),
  meal_alert_time: z.string().optional(),
  leave_alert_enabled: z.boolean().optional(),
  leave_alert_time: z.string().optional(),
  freshday_alert_enabled: z.boolean().optional(),
  freshday_alert_time: z.string().optional(),
  notify_deadline: z.boolean().optional(),
  notify_assignment: z.boolean().optional(),
  notify_status_change: z.boolean().optional(),
  theme: z.enum(['light', 'dark', 'system']).optional(),
  // 집계 발표자 담당 프로젝트 패널 접힘/펼침 기본값 (마지막 토글 상태 저장)
  portfolio_expanded: z.boolean().optional(),
  // 화이트보드 커서 색 '#RRGGBB'. null = 랜덤(접속 시 팔레트에서 배정)
  cursor_color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
  // 스토리보드 즐겨찾기 — 프로젝트 한정 [{ project_id, slot(1~10), label }]
  storyboard_favorites: z
    .array(
      z.object({
        project_id: z.number().int().positive(),
        slot: z.number().int().min(1).max(STORYBOARD_FAVORITE_MAX),
        label: z.string().max(120),
      }),
    )
    .max(STORYBOARD_FAVORITE_MAX)
    .optional(),
});

// === Agenda (회의록) ===

// due_date: 회의가 이루어져야 하는 기한. null = '기한 없음'(언젠가는 해야 할 과업).
// 클라이언트가 '기한 지정 / 기한 없음'을 라디오로 강제 선택시키므로 미입력과 구분된다.
const agendaDueDateField = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, '기한은 YYYY-MM-DD 형식이어야 합니다')
  .nullish();

export const agendaCreateSchema = z.object({
  title: z.string().min(1, '안건 제목을 입력해주세요').max(200),
  // 배경 설명 — 왜 이 안건이 올라왔는가. description(논의 안건)과 별개 필드.
  background: z.string().nullish(),
  description: z.string().nullish(),
  // 고객사 필수 — 사내 회의도 '한컴유비마이크로'를 선택한다. 회사별 회의 이력 조회의 전제.
  client_id: z.number().int().positive({ message: '고객사를 선택해주세요' }),
  due_date: agendaDueDateField,
  pm_id: z.number().int().positive().nullish(),
  suggested_attendee_ids: z.array(z.number().int().positive()).optional(),
  project_id: z.number().int().positive().nullish(),
});

export const agendaUpdateSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  background: z.string().nullish(),
  description: z.string().nullish(),
  // 수정 시에도 비울 수 없다(null 불가) — 고객사 미지정 과거 안건은 손대는 순간 정리된다
  client_id: z.number().int().positive().optional(),
  due_date: agendaDueDateField,
  pm_id: z.number().int().positive().nullish(),
  suggested_attendee_ids: z.array(z.number().int().positive()).optional(),
  project_id: z.number().int().positive().nullish(),
  version: z.number().int().positive(),
});

export const agendaStatusSchema = z.object({
  status: z.enum(AGENDA_STATUSES),
  close_comment: z.string().nullish(),
  version: z.number().int().positive(),
});

export const meetingSessionCreateSchema = z.object({
  meeting_date: z.string().nullish(),
  start_time: z.string().nullish(),
  end_time: z.string().nullish(),
  summary: z.string().nullish(),
  result: z.string().nullish(),
  decision: z.string().nullish(),
  nas_path: z.string().max(500).nullish(),
  attendee_user_ids: z.array(z.number().int().positive()).min(1, '참석자를 1명 이상 선택해주세요'),
});

export const meetingSessionUpdateSchema = z.object({
  meeting_date: z.string().nullish(),
  start_time: z.string().nullish(),
  end_time: z.string().nullish(),
  summary: z.string().nullish(),
  result: z.string().nullish(),
  decision: z.string().nullish(),
  nas_path: z.string().max(500).nullish(),
  attendee_user_ids: z.array(z.number().int().positive()).min(1, '참석자를 1명 이상 선택해주세요').optional(),
  version: z.number().int().positive(),
  auto_save: z.boolean().optional(),
});

export const emailDistributeSchema = z.object({
  recipient_mode: z.enum(['individual', 'leaders', 'all']),
  recipient_user_ids: z.array(z.number().int().positive()).optional(),
});

// 안건 배포 — 수신자 직접 선택 + 금번 회의 안건(발송 후 본문 최상단에 삽입)
export const agendaNotifySchema = z.object({
  recipient_user_ids: z.array(z.number().int().positive()).min(1, '수신자를 1명 이상 선택해주세요'),
  meeting_agenda: z.string().max(2000).nullish(),
});

// === Pagination & Sort ===

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  sort: z.string().optional(),
  order: z.enum(['asc', 'desc']).default('desc'),
});

// === Type exports ===
export type LoginInput = z.infer<typeof loginSchema>;
export type RegisterInput = z.infer<typeof registerSchema>;
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;
export type ProjectCreateInput = z.infer<typeof projectCreateSchema>;
export type ProjectUpdateInput = z.infer<typeof projectUpdateSchema>;
export type ProjectResponsibleMasterInput = z.infer<typeof projectResponsibleMasterSchema>;
export type ActivityCreateInput = z.infer<typeof activityCreateSchema>;
export type ActivityUpdateInput = z.infer<typeof activityUpdateSchema>;
export type ActivityUrgentInput = z.infer<typeof activityUrgentSchema>;
export type DeliverableUpdateInput = z.infer<typeof deliverableUpdateSchema>;
export type SettingsUpdateInput = z.infer<typeof settingsUpdateSchema>;
export type ActivityApproveInput = z.infer<typeof activityApproveSchema>;
export type ActivityRejectInput = z.infer<typeof activityRejectSchema>;
export type PaginationInput = z.infer<typeof paginationSchema>;
export type AgendaCreateInput = z.infer<typeof agendaCreateSchema>;
export type AgendaUpdateInput = z.infer<typeof agendaUpdateSchema>;
export type AgendaStatusInput = z.infer<typeof agendaStatusSchema>;
export type MeetingSessionCreateInput = z.infer<typeof meetingSessionCreateSchema>;
export type MeetingSessionUpdateInput = z.infer<typeof meetingSessionUpdateSchema>;
export type EmailDistributeInput = z.infer<typeof emailDistributeSchema>;
export type AgendaNotifyInput = z.infer<typeof agendaNotifySchema>;

export const meetingCommentCreateSchema = z.object({
  content: z.string().min(1, '코멘트를 입력해주세요').max(2000),
});
export type MeetingCommentCreateInput = z.infer<typeof meetingCommentCreateSchema>;

export const agendaCommentCreateSchema = z.object({
  content: z.string().min(1, '코멘트를 입력해주세요').max(2000),
});
export type AgendaCommentCreateInput = z.infer<typeof agendaCommentCreateSchema>;

// === Notice (공지사항) ===

export const noticeCreateSchema = z.object({
  title: z.string().min(1, '제목을 입력해주세요').max(200),
  content: z.string().nullish(),
});

export const noticeUpdateSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  content: z.string().nullish(),
  version: z.number().int().positive(),
});

export const noticeCommentCreateSchema = z.object({
  content: z.string().min(1, '코멘트를 입력해주세요').max(2000),
});

export type NoticeCreateInput = z.infer<typeof noticeCreateSchema>;
export type NoticeUpdateInput = z.infer<typeof noticeUpdateSchema>;
export type NoticeCommentCreateInput = z.infer<typeof noticeCommentCreateSchema>;

// === Issue Report (버그/개선 접수) ===

export const issueReportCreateSchema = z.object({
  title: z.string().min(1, '제목을 입력해주세요').max(200),
  description: z.string().min(1, '내용을 입력해주세요'),
  type: z.enum(ISSUE_REPORT_TYPES),
  is_urgent: z.boolean().default(false),
});

export const issueReportUpdateSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  description: z.string().min(1).optional(),
  type: z.enum(ISSUE_REPORT_TYPES).optional(),
  is_urgent: z.boolean().optional(),
  version: z.number().int().positive(),
});

export const issueReportStatusSchema = z.object({
  status: z.enum(ISSUE_REPORT_STATUSES),
  assignee_username: z.string().nullish(), // 승인 시 담당자 지정 (기본 js_heo)
  close_reason: z.string().max(500).nullish(), // 종결 시 사유
  version: z.number().int().positive(),
});

export const issueCommentCreateSchema = z.object({
  content: z.string().min(1, '코멘트를 입력해주세요').max(2000),
});

export type IssueReportCreateInput = z.infer<typeof issueReportCreateSchema>;
export type IssueReportUpdateInput = z.infer<typeof issueReportUpdateSchema>;
export type IssueReportStatusInput = z.infer<typeof issueReportStatusSchema>;
export type IssueCommentCreateInput = z.infer<typeof issueCommentCreateSchema>;

// === 프로젝트 등록 요청 ===
// 신청 폼: 예산(estimated_amount)·PM(pm_id)는 일반 사용자에게 노출하지 않음(마스터 권한 정보).
// project_number·status·activities도 제외(승인 시 자동 결정).
export const projectRequestCreateSchema = projectCreateSchema.omit({
  project_number: true,
  estimated_amount: true,
  pm_id: true,
  status: true,
  activities: true,
});

export const projectRequestUpdateSchema = projectRequestCreateSchema.partial().extend({
  version: z.number().int().positive(),
});

// 승인 시 마스터가 PM 확정(기본=신청자, 클라이언트에서 기본값 채움). 예산은 승인 폼에서 받지 않음.
export const projectRequestApproveSchema = z.object({
  pm_id: z.number().int().positive().nullish(),
  version: z.number().int().positive(),
});

// 반려 사유 필수
export const projectRequestRejectSchema = z.object({
  reason: z.string().min(1, '반려 사유를 입력해주세요').max(500),
  version: z.number().int().positive(),
});

export type ProjectRequestCreateInput = z.infer<typeof projectRequestCreateSchema>;
export type ProjectRequestUpdateInput = z.infer<typeof projectRequestUpdateSchema>;
export type ProjectRequestApproveInput = z.infer<typeof projectRequestApproveSchema>;
export type ProjectRequestRejectInput = z.infer<typeof projectRequestRejectSchema>;

// === Worklog (스토리보드 — 일일 업무 기록) ===

// 0.25 단위 검증 (부동소수 오차 회피 위해 정수 변환 후 모듈로)
const isQuarterStep = (v: number) => Math.round(v * 100) % 25 === 0;

// 실재하는 캘린더 날짜인지 검증 (2026-02-30, 2026-13-45 등 shape만 맞는 가짜 날짜 거부)
export const isValidIsoDate = (s: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
};

export const isValidIsoMonth = (s: string): boolean => {
  if (!/^\d{4}-\d{2}$/.test(s)) return false;
  const m = Number(s.slice(5, 7));
  return m >= 1 && m <= 12;
};

const worklogTagInputSchema = z.object({
  category_id: z.number().int().positive(),
  subcategory_id: z.number().int().positive().nullish(),
  source: z.enum(['auto', 'manual']).default('manual'),
});

export const worklogEntryCreateSchema = z.object({
  work_date: z.string().refine(isValidIsoDate, '존재하지 않는 날짜입니다 (YYYY-MM-DD)'),
  project_id: z.number().int().positive().nullish(),
  activity_id: z.number().int().positive().nullish(),
  summary: z.string().max(SUMMARY_MAX, `업무타이틀은 ${SUMMARY_MAX}자 이내로 입력해주세요`).default(''),
  hours: z
    .number()
    .positive('투입시간을 입력해주세요')
    .max(24, '하루 24시간을 초과할 수 없습니다')
    .refine(isQuarterStep, '투입시간은 0.25 단위로 입력해주세요'),
  content: z.string().max(CONTENT_MAX, `업무내용은 ${CONTENT_MAX}자 이내로 입력해주세요`).nullish(),
  work_location: z.enum(WORK_LOCATIONS).default('office'),
  tags: z.array(worklogTagInputSchema).max(20).optional(),
});

export const worklogEntryUpdateSchema = z.object({
  project_id: z.number().int().positive().nullish(),
  activity_id: z.number().int().positive().nullish(),
  summary: z.string().max(SUMMARY_MAX, `업무타이틀은 ${SUMMARY_MAX}자 이내로 입력해주세요`).optional(),
  hours: z
    .number()
    .positive('투입시간을 입력해주세요')
    .max(24, '하루 24시간을 초과할 수 없습니다')
    .refine(isQuarterStep, '투입시간은 0.25 단위로 입력해주세요')
    .optional(),
  content: z.string().max(CONTENT_MAX, `업무내용은 ${CONTENT_MAX}자 이내로 입력해주세요`).nullish(),
  work_location: z.enum(WORK_LOCATIONS).optional(),
  tags: z.array(worklogTagInputSchema).max(20).optional(),
  version: z.number().int().positive(),
});

// 선택한 기록 여러 건을 다른 날짜로 이동 (PATCH /worklogs/bulk-move).
// version을 받지 않는다 — 바꾸는 컬럼이 work_date 하나뿐이라 lost update가 성립하지 않고,
// 전건 원자 처리라 한 건의 stale version이 전체를 막는다. 상세 근거: docs/storyboard-bulk-move-copy-plan.md D2
export const worklogBulkMoveSchema = z.object({
  entry_ids: z
    .array(z.number().int().positive())
    .min(1, '이동할 기록을 선택해주세요')
    .max(WORKLOG_BULK_MAX, `한 번에 최대 ${WORKLOG_BULK_MAX}건까지 이동할 수 있습니다`),
  target_date: z.string().refine(isValidIsoDate, '존재하지 않는 날짜입니다 (YYYY-MM-DD)'),
});

// 지난 날짜의 기록을 대상 날짜로 복제 (POST /worklogs/bulk-copy).
// source_date를 함께 요구하는 이유: 모달이 (선택 날짜, 체크 집합) 두 상태를 들고 있어
// 날짜만 바꾸고 체크를 비우지 않는 버그가 가능하다. 전 행의 work_date와 대조해 조용한 오복제를 400으로 바꾼다.
export const worklogBulkCopySchema = z.object({
  source_date: z.string().refine(isValidIsoDate, '존재하지 않는 날짜입니다 (YYYY-MM-DD)'),
  target_date: z.string().refine(isValidIsoDate, '존재하지 않는 날짜입니다 (YYYY-MM-DD)'),
  entry_ids: z
    .array(z.number().int().positive())
    .min(1, '불러올 기록을 선택해주세요')
    .max(WORKLOG_BULK_MAX, `한 번에 최대 ${WORKLOG_BULK_MAX}건까지 불러올 수 있습니다`),
});

// 당일 근무정보 upsert — day_type(필수)과 business_trip(선택)은 같은 레코드의 직교 축이라
// 한 엔드포인트에서 함께 저장한다. business_trip을 생략하면 기존 값을 유지한다.
export const worklogDayTypeSchema = z.object({
  date: z.string().refine(isValidIsoDate, '존재하지 않는 날짜입니다 (YYYY-MM-DD)'),
  day_type: z.enum(DAY_TYPES),
  business_trip: z.boolean().optional(),
});

export const storyboardSettingsSchema = z.object({
  require_project: z.boolean().optional(),
  require_activity: z.boolean().optional(),
});

// 회의록 → 스토리보드 자동 연동 설정 (마스터). null 허용 = 해당 항목 해제.
// hub_project_id를 null로 두면 기능 전체가 비활성화된다.
export const agendaSyncSettingsSchema = z.object({
  hub_project_id: z.number().int().positive().nullable().optional(),
  category_id: z.number().int().positive().nullable().optional(),
  subcategory_id: z.number().int().positive().nullable().optional(),
});

// 회의록 게시판 운영 설정 (마스터). patch 의미론 — 넘어온 키만 갱신한다.
export const agendaBoardSettingsSchema = z.object({
  // 빈 문자열 저장 = 공지 내리기 (섹션 자체가 사라진다)
  notice_content: z.string().max(4000, '공지는 4000자를 넘을 수 없습니다').nullable().optional(),
  preset_label: z.string().min(1).max(30).optional(),
  preset_user_ids: z.array(z.number().int().positive()).max(20).optional(),
});

export type WorklogEntryCreateInput = z.infer<typeof worklogEntryCreateSchema>;
export type WorklogEntryUpdateInput = z.infer<typeof worklogEntryUpdateSchema>;
export type WorklogDayTypeInput = z.infer<typeof worklogDayTypeSchema>;
export type WorklogBulkMoveInput = z.infer<typeof worklogBulkMoveSchema>;
export type WorklogBulkCopyInput = z.infer<typeof worklogBulkCopySchema>;
export type StoryboardSettingsInput = z.infer<typeof storyboardSettingsSchema>;
export type AgendaSyncSettingsInput = z.infer<typeof agendaSyncSettingsSchema>;
export type AgendaBoardSettingsInput = z.infer<typeof agendaBoardSettingsSchema>;

// === Worklog Report (마스터 집계/조회 View — GET 쿼리 검증) ===
// validate() 미들웨어는 body만 검증하므로, 아래 스키마는 핸들러에서 req.query에 safeParse한다.

// CSV("3,5,12") → 정수 배열. 빈/무효 토큰 제거.
const csvIds = z
  .string()
  .optional()
  .transform((s) =>
    (s ?? '')
      .split(',')
      .map((t) => Number(t.trim()))
      .filter((n) => Number.isInteger(n) && n > 0),
  );

export const worklogReportGridQuerySchema = z.object({
  start: z.string().refine(isValidIsoDate, '존재하지 않는 날짜입니다 (YYYY-MM-DD)'),
  weeks: z.coerce.number().int().min(1).max(4).default(1),
  user_ids: csvIds,
  dept_id: z.coerce.number().int().positive().optional(),
  rank: z.string().trim().min(1).optional(),
  today: z.string().refine(isValidIsoDate, '존재하지 않는 날짜입니다 (YYYY-MM-DD)').optional(),
  // detail=1: 셀에 summary/project_name(짧은 라벨) 적재 — 업무 축(단일 사용자)의 좌측 라벨·활동 그룹핑용.
  //           시간/활동/상세 모드 공통(집계 people/project 모드는 미전송 → 페이로드 0 증가).
  detail: z.coerce.number().int().min(0).max(1).default(0),
  // body=1: 셀에 자유텍스트 본문(content) 적재 — 상세(detail) 모드에서만.
  //         자유텍스트는 E2 daily와 동일 거버넌스(교차 열람 감사) 대상이라 detail과 분리한다
  //         (시간/활동 모드에서 content가 무감사로 실려 나가 감사가 무력화되는 것을 방지). 0/1 숫자.
  body: z.coerce.number().int().min(0).max(1).default(0),
  // presenter=1: 발표 모드(아침 업무보고 순회) 중의 조회·선반입임을 표시 — 교차 열람 감사 스킵
  // (docs/storyboard-report-presenter-mode-plan.md, 2026-09-16 결정). 전 직원 도구로 확정되어
  // 발표 순회 자체는 감사 대상이 아니다. 클라 신고값이므로 결과 데이터에는 영향 없음(감사 여부만 좌우).
  presenter: z.coerce.number().int().min(0).max(1).default(0),
});

export const worklogReportDailyQuerySchema = z.object({
  date: z.string().refine(isValidIsoDate, '존재하지 않는 날짜입니다 (YYYY-MM-DD)'),
  user_id: z.coerce.number().int().positive(),
  // presenter=1: 발표 모드 중 조회 — 교차 열람 감사 스킵. 본인 열람은 presenter 여부와 무관하게
  // 라우터가 항상 스킵한다(설계 취지: "교차 열람만 감사").
  presenter: z.coerce.number().int().min(0).max(1).default(0),
});

// 발표자 담당 프로젝트 패널 (docs/storyboard-report-presenter-portfolio-plan.md)
export const worklogReportPortfolioQuerySchema = z.object({
  user_id: z.coerce.number().int().positive(),
  today: z.string().refine(isValidIsoDate, '존재하지 않는 날짜입니다 (YYYY-MM-DD)').optional(),
});

export const nonWorkingDaysSchema = z.object({
  days: z
    .array(
      z.object({
        date: z.string().refine(isValidIsoDate, '존재하지 않는 날짜입니다 (YYYY-MM-DD)'),
        label: z.string().trim().max(50).default(''),
        kind: z.enum(['holiday', 'company']).default('holiday'),
      }),
    )
    .max(366),
});

export const reportMinRoleSchema = z.object({
  min_role: z.enum(['user', 'master']),
});

/** 발표 순회 순서 — 전체 배열 교체(비근무일과 동일 방식). 배열 순서가 곧 발표 순서다. */
export const presenterOrderSchema = z.object({
  order: z
    .array(
      z.object({
        user_id: z.number().int().positive(),
        active: z.boolean().default(true),
      }),
    )
    .max(300),
});

// === 외부 연동 읽기전용 API (docs/integration-readonly-api-plan.md v4) ===

export const integrationLoginSchema = z.object({
  username: z.string().min(1, '아이디를 입력해주세요').max(50),
  password: z.string().min(1, '비밀번호를 입력해주세요').max(255),
});

export const integrationProjectsQuerySchema = z
  .object({
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    // 분류 코드는 DB 값 — 존재 여부가 아닌 형식만 검증한다
    classification: z.string().regex(/^[A-Za-z0-9]{1,2}$/, '분류 코드 형식이 아닙니다').optional(),
    status: z.enum(PROJECT_STATUSES).optional(),
    number: z.string().min(1).max(20).optional(),
  })
  .refine((q) => q.year !== undefined || q.number !== undefined, {
    message: 'year 파라미터가 필요합니다 (number 지정 시 생략 가능)',
  });

export const integrationActivitiesQuerySchema = z.object({
  // 명시 지정 시 기본 제외(provisional/rejected)보다 우선한다 (D7)
  status: z.enum(ACTIVITY_STATUSES).optional(),
});

export type IntegrationProjectsQuery = z.infer<typeof integrationProjectsQuerySchema>;
export type IntegrationActivitiesQuery = z.infer<typeof integrationActivitiesQuerySchema>;

export type WorklogReportGridQuery = z.infer<typeof worklogReportGridQuerySchema>;
export type WorklogReportDailyQuery = z.infer<typeof worklogReportDailyQuerySchema>;
export type WorklogReportPortfolioQuery = z.infer<typeof worklogReportPortfolioQuerySchema>;
export type NonWorkingDaysInput = z.infer<typeof nonWorkingDaysSchema>;
export type ReportMinRoleInput = z.infer<typeof reportMinRoleSchema>;
export type PresenterOrderInput = z.infer<typeof presenterOrderSchema>;
